import { z } from 'zod';
import { TimestampLike } from './timestamp-like';

// ── Contact (imported historical records) ──────────────────────────────────
// Path: organizations/{firmId}/contacts/{contactId}
// Imported via importContacts CF from CSV/Excel uploads.
// Required: (firstName + lastName) OR organizationName.

export const ContactRoleSchema = z.enum(['client', 'opposing_party', 'witness', 'other']);
export type ContactRole = z.infer<typeof ContactRoleSchema>;

export const ContactSchema = z.object({
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  middleName: z.string().nullable(),
  aliases: z.array(z.string()).nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(), // stored normalized (digits-only)
  organizationName: z.string().nullable(),
  role: ContactRoleSchema,
  matterName: z.string().nullable(),
  matterType: z.string().nullable(),
  matterDate: z.custom<TimestampLike>().nullable(),
  importedAt: z.custom<TimestampLike>(),
  importedBy: z.string(),
  importBatchId: z.string(),
  notes: z.string().nullable(),
});

export type Contact = z.infer<typeof ContactSchema>;

// ── Import Batch ───────────────────────────────────────────────────────────
// Path: organizations/{firmId}/importBatches/{batchId}

export const ImportBatchStatusSchema = z.enum(['processing', 'complete', 'error']);
export type ImportBatchStatus = z.infer<typeof ImportBatchStatusSchema>;

export const SkippedRowSchema = z.object({
  rowIndex: z.number().int().min(0),
  reason: z.string(),
});
export type SkippedRow = z.infer<typeof SkippedRowSchema>;

export const ImportBatchSchema = z.object({
  fileName: z.string(),
  storagePath: z.string(),
  uploadedAt: z.custom<TimestampLike>(),
  uploadedBy: z.string(),

  // Import phase progress (updated by Storage trigger)
  totalRows: z.number().int().min(0).nullable(),
  processedRows: z.number().int().min(0),
  recordCount: z.number().int().min(0),
  skippedCount: z.number().int().min(0),
  skippedRows: z.array(SkippedRowSchema).optional(),

  // Re-check phase progress (updated by Firestore trigger)
  reCheckTotal: z.number().int().min(0).nullable(),
  reCheckCompleted: z.number().int().min(0),

  status: ImportBatchStatusSchema,
  errorMessage: z.string().optional(),
});

export type ImportBatch = z.infer<typeof ImportBatchSchema>;

// ── Conflict Match ─────────────────────────────────────────────────────────
// Represents a single matching record found during a conflict check.

export const MatchTypeSchema = z.enum(['exact_email', 'exact_phone', 'name_similarity']);
export type MatchType = z.infer<typeof MatchTypeSchema>;

export const ConflictSeveritySchema = z.enum(['red', 'yellow']);
export type ConflictSeverity = z.infer<typeof ConflictSeveritySchema>;

export const ConflictMatchSchema = z.object({
  matchedRecordId: z.string(),
  matchedRecordType: z.enum(['inquiry', 'imported']),
  matchedName: z.string(),
  matchedAlias: z.string().nullable(), // the specific alias that triggered the match, if any
  matchedEmail: z.string().nullable(),
  matchedPhone: z.string().nullable(),
  matchedRole: ContactRoleSchema.nullable(), // role from imported record; null for inquiry matches
  matchedMatterName: z.string().nullable(),
  matchedStatus: z.string().nullable(), // inquiry status for inquiry matches
  matchTypes: z.array(MatchTypeSchema),
  nameScore: z.number().nullable(), // 0-1 Jaro-Winkler score, null for exact matches
  severity: ConflictSeveritySchema,
});

export type ConflictMatch = z.infer<typeof ConflictMatchSchema>;

// ── Conflict Result ────────────────────────────────────────────────────────
// Path: conflictResults/{sessionId}
//
// Document ID = intakeSessions document ID (O(1) lookup).
// Overwritten on each re-check. Before overwriting, the previous result is
// snapshotted to conflictResults/{sessionId}/history/{autoId} (on-demand only).

export const ConflictResultStatusSchema = z.enum(['pending', 'complete', 'error']);
export type ConflictResultStatus = z.infer<typeof ConflictResultStatusSchema>;

export const ConflictResultSeveritySchema = z.enum(['red', 'yellow', 'green']);
export type ConflictResultSeverity = z.infer<typeof ConflictResultSeveritySchema>;

export const ConflictSnapshotSchema = z.object({
  inquiryName: z.string(),
  inquiryEmail: z.string().nullable(),
  inquiryPhone: z.string().nullable(),
});

export type ConflictSnapshot = z.infer<typeof ConflictSnapshotSchema>;

export const ConflictResultSchema = z.object({
  sessionId: z.string(),
  organizationId: z.string(),
  status: ConflictResultStatusSchema,
  conflictSeverity: ConflictResultSeveritySchema.nullable(), // null while pending; enables where('conflictSeverity', '==', 'red') queries
  checkedAt: z.custom<TimestampLike>().nullable(), // null while pending
  checkedBy: z.string(), // 'system' for auto-checks, userId for manual re-checks
  totalRecordsSearched: z.number(),
  matches: z.array(ConflictMatchSchema),
  snapshot: ConflictSnapshotSchema,
});

export type ConflictResult = z.infer<typeof ConflictResultSchema>;

// ── Intake Session conflict summary fields ─────────────────────────────────
// Written to intakeSessions/{sessionId} on each check completion to enable
// efficient firm-wide queries without reading conflictResults docs individually.

export const IntakeSessionConflictSummarySchema = z.object({
  conflictStatus: ConflictResultStatusSchema,
  conflictSeverity: ConflictResultSeveritySchema.nullable(),
});

export type IntakeSessionConflictSummary = z.infer<typeof IntakeSessionConflictSummarySchema>;
